import { cn } from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import { Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import type { SocialName } from '../lib/site';
import { SOCIAL } from '../lib/site';

/**
 * Keyed by {@link SocialName}, not `string`: adding a network to `SOCIAL` without an
 * icon here is a compile error, instead of an `undefined` component that would throw
 * at render — and this shell wraps every route, so that crash would take the whole app.
 */
const ICONS: Record<SocialName, ComponentType<SVGProps<SVGSVGElement>>> = {
  Facebook,
  Instagram,
  YouTube: Youtube,
  LinkedIn: Linkedin,
};

const SIZES = {
  sm: { pill: 'h-7 w-7', icon: 'h-3.5 w-3.5' },
  md: { pill: 'h-9 w-9', icon: 'h-4 w-4' },
} as const;

/**
 * The brand site's round social pills, shared by the header bar and the footer so the
 * markup (and its contrast pairing) lives in one place.
 *
 * The hover fill pairs `bg-primary` with `text-primary-foreground`: the accent green
 * is a **fill color only** (docs/15 §7) — white on it is ≈2:1, far under the 3:1 floor
 * for a meaning-carrying glyph, so the fill must bring `--accent-fg` with it.
 */
export function SocialLinks({ size = 'md' }: { size?: keyof typeof SIZES }) {
  const s = SIZES[size];
  return (
    <ul aria-label={t('bidder.footer.follow')} className="flex items-center gap-2">
      {SOCIAL.map(({ name, href }) => {
        const Icon = ICONS[name];
        return (
          <li key={name}>
            <a
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              aria-label={name}
              className={cn(
                'flex items-center justify-center rounded-full bg-raised text-foreground hover:bg-primary hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                s.pill,
              )}
            >
              <Icon aria-hidden className={s.icon} />
            </a>
          </li>
        );
      })}
    </ul>
  );
}
